

import { Direction, Directionality } from '@angular/cdk/bidi';
import { MediaMatcher } from '@angular/cdk/layout';
import { Platform } from '@angular/cdk/platform';
import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Renderer2,
  SimpleChanges
} from '@angular/core';
import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { gridResponsiveMap, MwcBreakpointKey, MwcBreakpointService } from 'mwc/core/services';
import { IndexableObject } from 'mwc/core/types';

export type MwcJustify = 'start' | 'end' | 'center' | 'space-around' | 'space-between' | 'space-evenly';
export type MwcAlign = 'top' | 'middle' | 'bottom';

@Directive({
  selector: '[mwc-row],mwc-row,mwc-form-item',
  exportAs: 'mwcRow',
  host: {
    class: 'mwc-row',
    '[class.mwc-row-top]': `mwcAlign === 'top'`,
    '[class.mwc-row-middle]': `mwcAlign === 'middle'`,
    '[class.mwc-row-bottom]': `mwcAlign === 'bottom'`,
    '[class.mwc-row-start]': `mwcJustify === 'start'`,
    '[class.mwc-row-end]': `mwcJustify === 'end'`,
    '[class.mwc-row-center]': `mwcJustify === 'center'`,
    '[class.mwc-row-space-around]': `mwcJustify === 'space-around'`,
    '[class.mwc-row-space-between]': `mwcJustify === 'space-between'`,
    '[class.mwc-row-space-evenly]': `mwcJustify === 'space-evenly'`,
    '[class.mwc-row-rtl]': `dir === "rtl"`
  }
})
export class MwcRowDirective implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() mwcAlign: MwcAlign | null = null;
  @Input() mwcJustify: MwcJustify | null = null;
  @Input() mwcGutter: string | number | IndexableObject | [number, number] | [IndexableObject, IndexableObject] | null =
    null;

  readonly actualGutter$ = new ReplaySubject<[number | null, number | null]>(1);

  dir: Direction = 'ltr';
  private readonly destroy$ = new Subject();

  getGutter(): [number | null, number | null] {
    const results: [number | null, number | null] = [null, null];
    const gutter = this.mwcGutter || 0;
    const normalizedGutter = Array.isArray(gutter) ? gutter : [gutter, null];
    normalizedGutter.forEach((g, index) => {
      if (typeof g === 'object' && g !== null) {
        results[index] = null;
        Object.keys(gridResponsiveMap).map((screen: string) => {
          const bp = screen as MwcBreakpointKey;
          if (this.mediaMatcher.matchMedia(gridResponsiveMap[bp]).matches && g[bp]) {
            results[index] = g![bp] as number;
          }
        });
      } else {
        results[index] = Number(g) || null;
      }
    });
    return results;
  }

  setGutterStyle(): void {
    const [horizontalGutter, verticalGutter] = this.getGutter();
    this.actualGutter$.next([horizontalGutter, verticalGutter]);
    const renderGutter = (name: string, gutter: number | null): void => {
      const nativeElement = this.elementRef.nativeElement;
      if (gutter !== null) {
        this.renderer.setStyle(nativeElement, name, `-${gutter / 2}px`);
      }
    };
    renderGutter('margin-left', horizontalGutter);
    renderGutter('margin-right', horizontalGutter);
    renderGutter('margin-top', verticalGutter);
    renderGutter('margin-bottom', verticalGutter);
  }
  constructor(
    public elementRef: ElementRef,
    public renderer: Renderer2,
    public mediaMatcher: MediaMatcher,
    public ngZone: NgZone,
    public platform: Platform,
    private breakpointService: MwcBreakpointService,
    @Optional() private directionality: Directionality
  ) {}

  ngOnInit(): void {
    this.dir = this.directionality.value;
    this.directionality.change?.pipe(takeUntil(this.destroy$)).subscribe((direction: Direction) => {
      this.dir = direction;
    });

    this.setGutterStyle();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.mwcGutter) {
      this.setGutterStyle();
    }
  }

  ngAfterViewInit(): void {
    if (this.platform.isBrowser) {
      this.breakpointService
        .subscribe(gridResponsiveMap)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.setGutterStyle();
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
